import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    CloseButton,
    Dialog,
    HStack,
    IconButton,
    Portal,
    Text,
    VStack,
} from "@chakra-ui/react";
import { HiPaperClip, HiXMark } from "react-icons/hi2";
import { FormTextarea } from "@/components/jobOffer/ui";

const TIMELINE_KINDS = ["PhoneCall", "Meeting", "Info", "Verification", "ManualEntry"];

const KIND_COLOR = {
    PhoneCall: "brand.500",
    Meeting: "purple.400",
    Info: "gray.400",
    Verification: "green.500",
    ManualEntry: "yellow.500",
};

const AddTimelineEntryModal = ({
    isOpen,
    onClose,
    kind,
    setKind,
    description,
    setDescription,
    occurredAt,
    setOccurredAt,
    files,
    setFiles,
    onSubmit,
    isLoading,
}) => {
    const { t } = useTranslation("models");

    useEffect(() => {
        if (isOpen) {
            setOccurredAt(new Date().toISOString().slice(0, 16));
        }
    }, [isOpen, setOccurredAt]);

    const kindLabel = (k) => {
        const map = {
            PhoneCall: t("candidateDetailPage.timelineKindPhoneCall"),
            Meeting: t("candidateDetailPage.timelineKindMeeting"),
            Info: t("candidateDetailPage.timelineKindInfo"),
            Verification: t("candidateDetailPage.timelineKindVerification"),
            ManualEntry: t("candidateDetailPage.timelineKindManual"),
        };
        return map[k] ?? k;
    };

    return (
        <Dialog.Root
            open={isOpen}
            onOpenChange={(e) => {
                if (!e.open) onClose();
            }}
        >
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner p={4}>
                    <Dialog.Content
                        bg="white"
                        color="gray.900"
                        borderColor="gray.200"
                        border="1px solid"
                        boxShadow="0 4px 24px rgba(23,21,31,0.12)"
                        w={{ base: "calc(100vw - 2rem)", md: "lg" }}
                        maxW="100%"
                        maxH="calc(100dvh - 2rem)"
                        overflowY="auto"
                        borderRadius={12}
                    >
                        <Dialog.Body pt={6} pb={6} px={6}>
                            <Text fontWeight={700} fontSize="lg" mb={4} color="gray.900">
                                {t("candidateDetailPage.addTimelineEntryModalTitle")}
                            </Text>

                            <Text fontSize="sm" color="gray.500" mb={2}>
                                {t("candidateDetailPage.timelineKindSelectLabel")}
                            </Text>
                            <VStack align="stretch" gap={2} mb={4}>
                                {TIMELINE_KINDS.map((k) => (
                                    <Box
                                        key={k}
                                        as="button"
                                        type="button"
                                        onClick={() => setKind(k)}
                                        display="flex"
                                        alignItems="center"
                                        gap={3}
                                        px={3}
                                        py={2}
                                        borderRadius={8}
                                        border="1px solid"
                                        borderColor={kind === k ? "purple.400" : "gray.200"}
                                        bg={kind === k ? "purple.50" : "white"}
                                        cursor="pointer"
                                        transition="all 0.15s"
                                        _hover={{ borderColor: "purple.400" }}
                                        outline="none"
                                        textAlign="left"
                                    >
                                        <Box
                                            w="3px"
                                            h="20px"
                                            borderRadius="full"
                                            bg={KIND_COLOR[k]}
                                            flexShrink={0}
                                        />
                                        <Text fontSize="sm" color="gray.800">{kindLabel(k)}</Text>
                                    </Box>
                                ))}
                            </VStack>

                            <Text fontSize="sm" color="gray.500" mb={2}>
                                {t("candidateDetailPage.timelineEntryOccurredAtLabel")}
                            </Text>
                            <Box mb={4}>
                                <input
                                    type="datetime-local"
                                    value={occurredAt}
                                    onChange={(e) => setOccurredAt(e.target.value)}
                                    style={{
                                        width: "100%",
                                        background: "white",
                                        border: "1px solid var(--chakra-colors-gray-200, #e2e8f0)",
                                        borderRadius: "8px",
                                        color: "#1a202c",
                                        padding: "8px 12px",
                                        fontSize: "14px",
                                        colorScheme: "light",
                                    }}
                                />
                            </Box>

                            <Text fontSize="sm" color="gray.500" mb={2}>
                                {t("candidateDetailPage.timelineEntryDescriptionLabel")}
                            </Text>
                            <FormTextarea
                                placeholder={t("candidateDetailPage.timelineEntryDescriptionPlaceholder")}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={4}
                                resize="vertical"
                                mb={4}
                            />

                            <Box mb={4}>
                                <Text fontSize="sm" color="gray.500" mb={2}>
                                    {t("candidateDetailPage.timelineEntryAttachmentsLabel")}
                                </Text>
                                <Button
                                    as="label"
                                    htmlFor="timeline-file-input"
                                    variant="outlined"
                                    size="sm"
                                    cursor="pointer"
                                    color="gray.700"
                                    borderColor="gray.300"
                                    bg="white"
                                    _hover={{ bg: "purple.50", borderColor: "purple.300", color: "purple.700" }}
                                    mb={files.length > 0 ? 3 : 0}
                                >
                                    <HiPaperClip />
                                    {t("candidateDetailPage.timelineEntryAttachSelectFiles")}
                                    <input
                                        id="timeline-file-input"
                                        type="file"
                                        multiple
                                        style={{ display: "none" }}
                                        onChange={(e) => {
                                            const picked = Array.from(e.target.files ?? []);
                                            setFiles((prev) => [...prev, ...picked]);
                                            e.target.value = "";
                                        }}
                                    />
                                </Button>
                                {files.length > 0 && (
                                    <VStack align="stretch" gap={1}>
                                        {files.map((f, i) => (
                                            <HStack
                                                key={i}
                                                bg="gray.50"
                                                border="1px solid"
                                                borderColor="gray.200"
                                                borderRadius={8}
                                                px={3}
                                                py={2}
                                                justify="space-between"
                                            >
                                                <HStack gap={2} minW={0}>
                                                    <Box color="gray.400" flexShrink={0}><HiPaperClip /></Box>
                                                    <Text fontSize="xs" color="gray.700" fontFamily="mono" truncate>{f.name}</Text>
                                                </HStack>
                                                <IconButton
                                                    variant="ghost"
                                                    size="xs"
                                                    aria-label={t("candidateDetailPage.timelineEntryRemoveFile")}
                                                    color="gray.400"
                                                    flexShrink={0}
                                                    _hover={{ bg: "red.50", color: "red.500" }}
                                                    onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                                                >
                                                    <HiXMark />
                                                </IconButton>
                                            </HStack>
                                        ))}
                                    </VStack>
                                )}
                            </Box>

                            <HStack justify="flex-end" gap={2}>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    color="gray.600"
                                    _hover={{ bg: "gray.100" }}
                                    onClick={onClose}
                                    disabled={isLoading}
                                >
                                    {t("candidateDetailPage.timelineEntryCancel")}
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    loading={isLoading}
                                    disabled={!kind || !description.trim()}
                                    onClick={onSubmit}
                                >
                                    {t("candidateDetailPage.timelineEntrySubmit")}
                                </Button>
                            </HStack>
                        </Dialog.Body>
                        <Dialog.CloseTrigger asChild>
                            <CloseButton size="sm" position="absolute" top="2" right="2" color="gray.400" _hover={{ bg: "gray.100", color: "gray.600" }} />
                        </Dialog.CloseTrigger>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};

export default AddTimelineEntryModal;
